import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const YourAvailability = () => (
  <Page
    title="Your Availability"
    intro="My Availability is where you tell planners when you can work and
    when you can't — availability windows, annual leave, sickness, and other
    time off — and where you keep an eye on your holiday balance."
  >
    <Section title="Telling planners when you can work">
      <p>
        On <DocLink text="My Availability" url="/my-availability" />, choose{' '}
        <strong>Available</strong> and pick the window you can work.
        Available windows are recorded straight away — no approval needed —
        and show up on your planners' availability views, so saying when you
        can work genuinely makes you easier to roster.
      </p>
      <Screenshot url="workforce/my-availability.png">
        My Availability with your records, holiday balance, and the Available
        and Unavailable entry points.
      </Screenshot>
    </Section>

    <Section title="Time off and unavailability">
      <p>
        The <strong>Unavailable</strong> flow covers the periods you cannot
        work, with a type, a reason, and notes:
      </p>
      <Bullets>
        <Bullet>
          <strong>Sickness</strong> is recorded straight away.
        </Bullet>
        <Bullet>
          <strong>Annual Leave</strong>, <strong>Unavailable</strong>, and{' '}
          <strong>Other</strong> go to your manager for approval. The period
          is reserved while the request waits, and each record shows its
          status — Pending, Approved, Rejected, or Cancelled.
        </Bullet>
      </Bullets>
      <p>
        Two of your records can't cover the same time — an overlapping
        submission is refused, so adjust or cancel the existing record first.
      </p>
    </Section>

    <Section title="Your holiday balance">
      <p>
        If your employer records your leave entitlement, My Availability also
        shows your holiday balance for the current leave year: entitlement,
        leave accrued so far, approved annual leave, pending requests,
        remaining leave, and your projected remaining leave if the pending
        requests are approved. Accrual is calculated from your employment
        dates and approved annual leave.
      </p>
    </Section>

    <Section title="Changing your mind">
      <p>
        You can <strong>Cancel</strong> any of your own records while the
        period hasn't ended yet — a cancelled request simply disappears from
        planners' views. Once a period has ended it is history, and only a
        manager can amend it.
      </p>
    </Section>

    <Section title="What availability does not do">
      <Info title="Availability is information, not a booking">
        An Available window never books you onto anything — it tells planners
        you could work, and helps them pick you. Actual work still arrives as
        a shift you apply for or are assigned to: see{' '}
        <DocLink docsPage url="/crew/your-shifts" text="Your Shifts" />.
      </Info>
    </Section>

    <Section title="Who can do this?">
      <Info title="Who can do this?">
        Any signed-in staff member can manage their own availability.
        Reviewing leave requests and seeing the whole team's availability is
        a manager's job — planning rosters? See{' '}
        <DocLink
          docsPage
          url="/employees-hr/rostering-payroll"
          text="Rostering, Availability, and Payroll"
        />
        .
      </Info>
    </Section>
  </Page>
)

export default YourAvailability
